import { memo, useState, useCallback, useContext, useEffect, useRef } from 'react';

import { Checkbox, SectionForm, Text } from '@Sage/hoi-poi-ui';
import { isEmptyObject } from '@Sage/ssm-utils';

import SelectHoi from 'components/SelectHoi';
import { getAutomationVariables } from 'services/AutomationsService';
import { getLiteral } from 'utils/getLiteral';
import { errorToast } from 'utils/toast';

import { resolveAutomationEntityIcon, resolveAutomationTriggerIcon } from '../../../utils';
import { AutomationBuilderCanvasContext } from '../AutomationBuilderCanvasContext';
import AutomationBuilderReEntryRow from '../components/AutomationBuilderReEntryRow';
import AutomationBuilderSidePanel from './AutomationBuilderSidePanel';

const AutomationBuilderTriggerSidePanel = memo(
    ({ data, onChange, onClose, openDiscard, isTouched, setTouched }) => {
        const { persistEveryTime } = useContext(AutomationBuilderCanvasContext);
        const first = useRef(true);
        const [events, setEvents] = useState();
        const [actions, setActions] = useState();
        const [everyTime, setEveryTime] = useState(data?.scene?.parameters?.everyTime ?? false);
        const [form, setForm] = useState({
            withCreation: data?.scene?.parameters?.withCreation,
            entity: data?.scene?.parameters?.entity
                ? {
                      label: getLiteral(data?.scene?.parameters?.entity?.description),
                      value: data?.scene?.parameters?.entity?.value,
                      iconType: resolveAutomationEntityIcon(data?.scene?.parameters?.entity?.value),
                      original: data?.scene?.parameters?.entity,
                  }
                : null,
            action: data?.scene?.parameters?.action
                ? {
                      label: getLiteral(data?.scene?.parameters?.action?.title),
                      subLabel: getLiteral(data?.scene?.parameters?.action?.description),
                      value: data?.scene?.parameters?.action?.value,
                      iconType: resolveAutomationTriggerIcon(
                          data?.scene?.parameters?.action?.icon,
                          data?.scene?.parameters?.action?.value,
                      ),
                      original: data?.scene?.parameters?.action,
                  }
                : null,
        });

        const onChangeReEntry = useCallback(
            (nextEveryTime) => {
                if (typeof nextEveryTime !== 'boolean' || everyTime === nextEveryTime) return;

                setEveryTime(nextEveryTime);
                persistEveryTime?.(nextEveryTime);
            },
            [everyTime, persistEveryTime],
        );

        useEffect(() => {
            setEveryTime(data?.scene?.parameters?.everyTime ?? false);
        }, [data?.scene?.parameters?.everyTime]);

        useEffect(() => {
            if (!first.current) return;
            first.current = false;
            getAutomationVariables({ name: data.scene.name, type: data.scene.type })
                .then((events) => {
                    const newEvents = events?.map((event) => {
                        const newEvent = {
                            original: event.entity,
                            label: getLiteral(event.entity.description),
                            value: event.entity.value,
                            iconType: resolveAutomationEntityIcon(event.entity.value),
                            actions: event.action?.map((action) => ({
                                label: getLiteral(action.title),
                                subLabel: getLiteral(action.description),
                                value: action.value,
                                iconType: resolveAutomationTriggerIcon(action.icon, action.value),
                                original: action,
                            })),
                        };

                        if (newEvent.value === data?.scene?.parameters?.entity?.value)
                            setActions(newEvent.actions);

                        return newEvent;
                    });
                    setEvents(newEvents);
                })
                .catch((e) => {
                    console.error(e);
                    errorToast({ text: getLiteral('error_generalerror') });
                });
        }, [data]);

        const changeForm = useCallback(
            (field) => (value, valueObject) => {
                setTouched(true);

                if (field === 'entity') {
                    setActions(valueObject?.actions);
                    setForm((current) => {
                        const entityChanged = current.entity?.value !== value?.value;

                        return {
                            ...current,
                            entity: value,
                            action: entityChanged ? null : current.action,
                            withCreation: entityChanged ? undefined : current.withCreation,
                        };
                    });
                    return;
                }
                setForm((current) => ({
                    ...current,
                    action: value,
                    withCreation: value?.original?.canCreate,
                }));
            },
            [setTouched],
        );

        const onDone = useCallback(() => {
            const newData = {
                entity: form.entity.original,
                action: form.action.original,
                withCreation: form.withCreation,
                everyTime,
            };
            const triggerDefinitionChanged =
                newData.entity?.value !== data?.scene?.parameters?.entity?.value ||
                newData.action?.value !== data?.scene?.parameters?.action?.value ||
                newData.withCreation !== data?.scene?.parameters?.withCreation;

            const hasNonEmptyNodes = (node) => {
                if (!node) return false;

                if (node.parameters && !isEmptyObject(node.parameters) && node.type !== 'trigger') {
                    return true;
                }

                if (node.next && Array.isArray(node.next)) {
                    return node.next.some((nextNode) => hasNonEmptyNodes(nextNode));
                }

                return false;
            };

            if (triggerDefinitionChanged && hasNonEmptyNodes(data?.scene)) {
                openDiscard(() => {
                    onChange('scene', {
                        ...data?.scene,
                        parameters: newData,
                        _isIncomplete: false,
                    });

                    // Clear all nodes in the tree recursively
                    const clearNode = (node, path) => {
                        if (!node) return;
                        if (node.type !== 'trigger') {
                            onChange(`${path}.parameters`, {});
                        }

                        // Recursively clear next nodes
                        if (node.next && Array.isArray(node.next)) {
                            node.next.forEach((nextNode, index) => {
                                clearNode(nextNode, `${path}.next.${index}`);
                            });
                        }
                    };

                    // Start clearing from the first node after trigger
                    if (data?.scene?.next && Array.isArray(data.scene.next)) {
                        data.scene.next.forEach((nextNode, index) => {
                            clearNode(nextNode, `scene.next.${index}`);
                        });
                    }
                    setTouched(false);
                });
            } else {
                onChange('scene', {
                    ...data?.scene,
                    parameters: newData,
                    _isIncomplete: false,
                });
                setTouched(false);
            }
        }, [
            data,
            form?.entity?.original,
            form?.action?.original,
            form?.withCreation,
            everyTime,
            openDiscard,
            onChange,
            setTouched,
        ]);

        const creationOnChange = useCallback(() => {
            setTouched(true);
            setForm((current) => ({ ...current, withCreation: !current.withCreation }));
        }, [setTouched]);

        return (
            <AutomationBuilderSidePanel
                title={getLiteral('label_trigger')}
                subtitle={getLiteral('label_trigger_desc')}
                onClose={onClose}
                isDoneDisabled={!isTouched || !form?.action || !form?.entity}
                onDone={onDone}
            >
                <SectionForm title={getLiteral('label_trigger')} isExpandable={false}>
                    <SelectHoi
                        label={getLiteral('label_triggering_entity')}
                        placeholder={getLiteral('label_placeholder_entity')}
                        options={events}
                        onChange={changeForm('entity')}
                        value={form.entity}
                        useMenuPortal={false}
                        isFullWidth
                    />
                    <SelectHoi
                        label={getLiteral('label_triggering_event')}
                        placeholder={getLiteral('label_placeholder_event')}
                        options={actions}
                        onChange={changeForm('action')}
                        value={form.action}
                        isFullWidth
                        useMenuPortal={false}
                        isReadOnly={!form.entity}
                    />
                    {form.action?.original?.canCreate && (
                        <div className="fm-automation-builder__checkbox" onClick={creationOnChange}>
                            <Checkbox checked={form.withCreation} color="actionMinor" />
                            <Text>{getLiteral('label_automation_event_creation_included')}</Text>
                        </div>
                    )}
                </SectionForm>
                <AutomationBuilderReEntryRow
                    checked={everyTime}
                    interactive
                    onChange={onChangeReEntry}
                    variant="panel"
                />
            </AutomationBuilderSidePanel>
        );
    },
);

AutomationBuilderTriggerSidePanel.displayName = 'AutomationBuilderTriggerSidePanel';

export default AutomationBuilderTriggerSidePanel;
